import React, { ReactNode } from "react";

import { css } from "@compiled/react";
import { ReactComponentLike } from "prop-types";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components";
import type {
    ButtonVariant,
    ButtonColor
} from "@/libraries/design/components/button/src/button";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ArrowRightNoCircle } from "@/libraries/wpl/foundations/src/icons/basic/arrow-right-no-circle";
import { ScrollTracking } from "@/utils/scroll-tracking/scroll-tracking";

export type CtaButtonTypes = {
    buttonText: React.ReactNode;
    buttonColor: ButtonColor;
    buttonHref: string;
    buttonVariant: ButtonVariant;
    eventProps?: {
        label: string;
        labelEnglish: string;
    };
};

export type TabList = {
    title: string;
    icon: ReactNode;
    iconHover?: ReactNode;
    content: ReactComponentLike;
    lozenge?: string;
    eventProps?: {
        label?: string;
        labelEnglish?: string;
    };
};

export type ReusableCrossflowHeroComponentProps = {
    heroTitle?: React.ReactNode;
    heroSubtitle?: React.ReactNode;
    ctaButton?: CtaButtonTypes;
    contentComponent?: React.ComponentType<Record<string, never>>;
    segmentName?: string;
    sectionId?: string;
    scrollTrackingSection?: string;
    gwp10965XflowRecommendation?: boolean;
};

const contactSalesMessage = {
    id: "HomePageTemplateV2.crossflowHero.contactSales",
    defaultMessage: "Contact Sales"
};

export const ContactSalesLink = () => {
    const intl = useIntl();
    const label = intl.formatMessage(contactSalesMessage);

    return (
        <a
            href={useLocalizedHref("/enterprise/contact")}
            css={contactSalesLinkStyles}
            aria-label={label}
            data-event="clicked"
            data-event-component="linkButton"
            data-event-container="crossFlowHeroSection"
            data-product-key="home"
            data-label-english="Contact sales"
            data-label={label}
        >
            {label}
            <span css={contactSalesArrowStyles}>
                <ArrowRightNoCircle />
            </span>
        </a>
    );
};

export const ReusableCrossflowHeroComponent = ({
    heroTitle,
    heroSubtitle,
    ctaButton,
    contentComponent: ContentComponent,
    segmentName = "reusable-crossflow-hero",
    sectionId = "reusable-crossflow-hero",
    scrollTrackingSection = "reusable-crossflow-hero",
    gwp10965XflowRecommendation = false
}: ReusableCrossflowHeroComponentProps) => {
    return (
        <UFOSegment name={segmentName}>
            <ScrollTracking section={scrollTrackingSection}>
                <section id={sectionId} css={crossflowCTAStyles}>
                    <div css={heroTitleContainerStyles}>
                        <h1
                            className="superhero-contrast"
                            css={heroTitleStyles}
                        >
                            {heroTitle}
                        </h1>
                        {heroSubtitle}
                        {ctaButton && (
                            <div css={ctaContainerStyles}>
                                <Button
                                    variant={
                                        ctaButton.buttonVariant || "primary"
                                    }
                                    href={ctaButton.buttonHref}
                                    color={ctaButton.buttonColor || "blue"}
                                    aria-label={
                                        (ctaButton.buttonText as string) ??
                                        "Get started"
                                    }
                                    aria-describedby={"cta-button"}
                                    dataEventProps={{
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container":
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            "crossFlowHeroSection" as any,
                                        "data-product-key": "home",
                                        "data-label-english":
                                            ctaButton.eventProps?.labelEnglish,
                                        "data-label":
                                            ctaButton.eventProps?.label
                                    }}
                                >
                                    {ctaButton.buttonText}
                                </Button>
                                {gwp10965XflowRecommendation && (
                                    <ContactSalesLink />
                                )}
                            </div>
                        )}
                    </div>
                    <div css={contentTypesSectionStyles}>
                        {ContentComponent && <ContentComponent />}
                    </div>
                </section>
            </ScrollTracking>
        </UFOSegment>
    );
};

const heroTitleContainerStyles = css({
    display: "grid",
    justifyItems: "center",
    marginBlockStart: "32px",
    marginBlockEnd: "48px",

    [media.below.sm]: {
        marginBlockStart: "16px",
        marginBlockEnd: "32px"
    }
});

const heroTitleStyles = css({
    marginBlock: "19px",
    textAlign: "center",
    [media.below.sm]: {
        marginInlineStart: "16px",
        marginInlineEnd: "16px"
    }
});

const ctaContainerStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "32px",
    marginBlockStart: "16px"
});

const contactSalesLinkStyles = css({
    "display": "inline-flex",
    "alignItems": "center",
    "gap": "4px",
    "fontSize": "16px",
    "fontWeight": 500,
    "color": wacToken("color.extended.blue700"),
    "textDecoration": "none",
    "&:hover": {
        gap: "8px"
    }
});

const contactSalesArrowStyles = css({
    display: "inline-flex",
    alignItems: "center"
});

const crossflowCTAStyles = css({
    textAlign: "center",
    overflow: "hidden",
    marginBlockEnd: token("space.800")
});

const contentTypesSectionStyles = css({
    marginBlockStart: "8px",
    textAlign: "left"
});

export default ReusableCrossflowHeroComponent;
